import React from "react";
import type { ApolloClient } from "@apollo/client";

import { Formik } from "formik";
import type { FormikHelpers } from "formik";
import { get } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import { userConstants } from "actions/types";
import { ScrollToFieldError } from "utils/form";
import { Card, Col, Form, Row } from "react-bootstrap";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { createSupportRequest } from "actions/userActions";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import { distributionTypesToEnglishMapping } from "statics/accountTypes";
import type { RootState } from "types/state";
import type { DistributionAmountSelection } from "./distributionTypes";

import Alert from "components/Alert";
import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";

import * as yup from "yup";

const schema = yup.object({
  subject: yup
    .string()
    .label("Subject")
    .required()
    .min(2, "Must be at least two characters.")
    .max(200, "Must be less than 200 characters."),
  comment: yup
    .string()
    .label("comment")
    .required()
    .min(2, "Must be at least two characters.")
    .max(1000, "Must be less than 1000 characters."),
});

const actions = [userConstants.CREATE_REQUEST];

const loadingSelector = createLoadingSelector(actions);
// PRESERVED QUIRK (conversion-only): `actions` is already a string[], so
// `[actions]` passes a nested string[][] where string | string[] is expected.
// It still resolves for this single-element array — the inner array coerces to
// "CREATE_REQUEST" in the `error.${action}` lookup, so the error IS tracked —
// but it is fragile if `actions` ever grows. Matches the original JS; do not
// "fix" the wrapping.
// @ts-expect-error preserved nested-array argument
const errorSelector = createErrorSelector([actions]);

const mapStateToProps = (state: RootState) => {
  // profile is typed { dob?; userIsFiftyOrOlder? } & Record<string, unknown>;
  // the original assumed these string keys are present, so assert string here
  const { firstName, lastName, email } = get(state, "user.profile", {}) as {
    firstName: string;
    lastName: string;
    email: string;
  };

  const accounts = state.accounts.iraAccounts;
  const userState = (state.user.userState as { state?: string }).state;

  return {
    email,
    name: `${firstName} ${lastName}`,
    userState,
    error: errorSelector(state),
    isSubmitting: loadingSelector(state),
    accounts,
  };
};

const mapDispatchToProps = {
  push,
  createSupportRequest,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  goToNamedStep?: (stepName: string) => void;
  selectedAccountType?: string;
  // PRESERVED BUG (conversion-only): original PropTypes declared this as
  // `string`, but the code reads `distributionAmount.amount` — it is an object.
  // The workflows pass their full distributionAmountSelection here.
  distributionAmount: DistributionAmountSelection;
  userHasTwoAccounts?: boolean;
  rolloverSupportFlag?: boolean;
  isProcessingRollover?: boolean;
  prefilledSubject?: string;
  distributionType?: string;
  isAccountClosure?: boolean;
  // react-step-wizard injects stepName at runtime; the workflow declares it
  // on the JSX element.
  stepName?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialSubject: string;
  initialComment: string;
}

interface SupportFormValues {
  subject: string;
  comment: string;
}

class DistributionSupport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const accountType = get(
      this.props.accounts.find(
        (account) => account.id === this.props.selectedAccountType
      ),
      "accountType",
      ""
    );

    const accountNumber = get(
      this.props.accounts.find(
        (account) => account.id === this.props.selectedAccountType
      ),
      "accountNumber",
      ""
    );
    const amount = this.props.distributionAmount.amount || "";

    const distributionType =
      distributionTypesToEnglishMapping[
        this.props
          .distributionType as keyof typeof distributionTypesToEnglishMapping
      ];

    this.state = {
      initialSubject: `Distribution Request: ${distributionType}`,
      initialComment: `${
        accountTypesToEnglishMapping[
          accountType as keyof typeof accountTypesToEnglishMapping
        ]
      } (${accountNumber}) - $${amount}`,
    };
  }

  _submitRequest = (
    vals: SupportFormValues,
    { resetForm }: FormikHelpers<SupportFormValues>
  ) => {
    const requestParams = {
      comment: vals.comment,
      subject: vals.subject,
      name: this.props.name,
      email: this.props.email,
    };

    void this.props
      .createSupportRequest(
        this.props.client as ApolloClient<object>,
        requestParams
      )
      .then(() => {
        if (!this.props.error) {
          toast.success("Support request submitted. You'll get an email.");
          resetForm();
          if (this.props.isAccountClosure) {
            this.props.push("/dashboard/profile/close-account");
          }
        }
      });
  };

  _supportTicket = () => {
    var articleStyle = {
      paddingBottom: 0,
    };

    return (
      <div className="transactions-mega-container">
        <section className="form-sec-2col">
          <article className="col-form" style={articleStyle}>
            <div>
              <Card>
                <Card.Title style={{ padding: 20, margin: 0 }}>
                  <IconHeader
                    variant="cardHeader"
                    headerText="How can we assist with your distribution?"
                  />
                  <IconSubheader subheader="Please describe what you need help with so we can better assist you with your distribution, and we'll respond via email as soon as possible." />
                </Card.Title>

                <Card.Body>
                  <Formik
                    validateOnChange={false}
                    validationSchema={schema}
                    onSubmit={this._submitRequest}
                    enableReinitialize={true}
                    initialValues={{
                      subject: this.state.initialSubject,
                      comment: this.state.initialComment,
                    }}
                  >
                    {({
                      handleSubmit,
                      handleChange,
                      values,
                      touched,
                      errors,
                    }) => (
                      <Form noValidate onSubmit={handleSubmit}>
                        <ScrollToFieldError />

                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formGroupSubject">
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                              name="subject"
                              value={values.subject}
                              onChange={handleChange}
                              isInvalid={!!errors.subject}
                              isValid={touched.subject && !errors.subject}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.subject}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formGroupComment">
                            <Form.Label>Message</Form.Label>

                            <Form.Control
                              name="comment"
                              as="textarea"
                              placeholder="Please include any information you think will be helpful."
                              value={values.comment}
                              onChange={handleChange}
                              isInvalid={!!errors.comment}
                              style={{ minHeight: "150px" }}
                              isValid={touched.comment && !errors.comment}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.comment}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>

                        {this.props.error && (
                          <Alert type="error" msg={this.props.error} />
                        )}
                        <div className="submit-row btn-row">
                          {/* Button `name` is required in ButtonProps but optional in practice (spread to <button name={undefined}>); original markup omitted it — pre-existing inert prop omission. */}
                          {/* @ts-expect-error preserved missing required `name` prop */}
                          <Button
                            type="button"
                            btnLabel={"Cancel"}
                            color={"cancel"}
                            onClick={() =>
                              this.props.goToNamedStep!("distributionType")
                            }
                          />
                          <Button
                            name="submit"
                            btnLabel="Submit"
                            withArrow={true}
                            loading={this.props.isSubmitting}
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            </div>
          </article>
        </section>
      </div>
    );
  };

  render() {
    return <>{this._supportTicket()}</>;
  }
}

export default connector(withApollo<Props>(DistributionSupport));
